import React, { useEffect, useState } from 'react';
import { api } from '../../api.js';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import { formatDate } from '../../format.js';
import { openPrivateFile } from '../../privateFile.js';
import { DOC_TYPES } from '../../companyOptions.js';
import { Alert, Field } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { AccountHead, useAccount } from './AccountLayout.jsx';

const ACCEPT = 'application/pdf,image/jpeg,image/png';
const MAX = 10 * 1024 * 1024;
const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.ceil(n / 1024)} KB`);
const blankMeta = { type: 'registration', number: '', issuedOn: '', expiresOn: '', visibility: 'private' };
const daysTo = (d) => (d ? Math.ceil((new Date(`${d}T00:00:00`) - new Date(new Date().toDateString())) / 86400000) : null);

// Documents de l'entreprise: PDF / JPG / PNG (10 MB max each) with type,
// number, issue / expiry dates and visibility (public on the company page
// once verified, or private: PME verification only). PME Trade Canada sets
// the status (En vérification / Vérifié / Refusé). Files are private:
// only the member and admins can open them (public ones once verified).
export default function DocumentsPage() {
  const { t, lang, errorText } = useI18n();
  const { reload } = useAccount();
  usePageTitle('accountNav.documents');
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [pending, setPending] = useState(null); // { file, meta } before upload
  const [editing, setEditing] = useState(null); // { id, meta }
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState(false);

  const apply = (d) => setDocs(d.documents);
  useEffect(() => { api('/membership/company').then(apply).catch((e) => setError(errorText(e))); }, [errorText]);

  const pick = (file) => {
    setError(''); setToast('');
    if (!file) return;
    if (!/\.(pdf|jpe?g|png)$/i.test(file.name) && !['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) { setError(t('errors.invalid_document')); return; }
    if (file.size > MAX) { setError(t('errors.file_too_large')); return; }
    setPending({ file, meta: { ...blankMeta } });
  };
  async function upload() {
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      for (const [k, v] of Object.entries(pending.meta)) fd.append(k, v);
      fd.append('document', pending.file, pending.file.name);
      apply(await api('/membership/documents', { method: 'POST', form: fd }));
      setPending(null); setToast(t('documentsPage.uploaded')); reload();
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  }
  async function saveMeta() {
    setBusy(true); setError('');
    try { apply(await api(`/membership/documents/${editing.id}`, { method: 'PATCH', body: editing.meta })); setEditing(null); setToast(t('common.saved')); reload(); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  }
  async function remove(d) {
    setBusy(true);
    try { apply(await api(`/membership/documents/${d.id}`, { method: 'DELETE' })); setConfirm(null); setToast(t('documentsPage.deleted')); reload(); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  }

  const metaForm = (meta, setMeta, idp) => (
    <div className="acc-page" style={{ gap: 12 }}>
      <div className="grid-form">
        <Field label={t('documentsPage.type')} htmlFor={`${idp}-type`}>
          <select id={`${idp}-type`} value={meta.type} onChange={(e) => setMeta({ ...meta, type: e.target.value })}>
            {DOC_TYPES.map((x) => <option key={x} value={x}>{t(`documentsPage.types.${x}`)}</option>)}
          </select>
        </Field>
        <Field label={t('documentsPage.number')} optional htmlFor={`${idp}-num`}><input id={`${idp}-num`} value={meta.number} onChange={(e) => setMeta({ ...meta, number: e.target.value })} maxLength={80} placeholder={t(`documentsPage.numberPlaceholder.${meta.type}`)} /></Field>
      </div>
      <div className="grid-form">
        <Field label={t('documentsPage.issuedOn')} optional htmlFor={`${idp}-iss`}><input id={`${idp}-iss`} type="date" value={meta.issuedOn || ''} onChange={(e) => setMeta({ ...meta, issuedOn: e.target.value })} /></Field>
        <Field label={t('documentsPage.expiresOn')} optional hint={t('documentsPage.expiresHint')} htmlFor={`${idp}-exp`}><input id={`${idp}-exp`} type="date" value={meta.expiresOn || ''} onChange={(e) => setMeta({ ...meta, expiresOn: e.target.value })} /></Field>
      </div>
      <div className="field" role="radiogroup" aria-labelledby={`${idp}-vis`}>
        <span className="field-label" id={`${idp}-vis`}>{t('documentsPage.visibility')}</span>
        <div className="acc-seg">
          {['private', 'public'].map((v) => <button key={v} type="button" role="radio" aria-checked={meta.visibility === v} aria-pressed={meta.visibility === v} onClick={() => setMeta({ ...meta, visibility: v })}>{t(`documentsPage.vis.${v}`)}</button>)}
        </div>
        <p className="field-hint">{t(`documentsPage.visHint.${meta.visibility}`)}</p>
      </div>
    </div>
  );

  return (
    <div className="acc-page">
      <AccountHead place="acc.documents" title={t('accountNav.documents')} lead={t('documentsPage.lead')} />
      <Alert kind="success">{toast}</Alert>
      <Alert>{error}</Alert>

      {!pending ? (
        <label className={`acc-drop${hover ? ' is-hover' : ''}`} onDragOver={(e) => { e.preventDefault(); setHover(true); }} onDragLeave={() => setHover(false)}
          onDrop={(e) => { e.preventDefault(); setHover(false); pick(e.dataTransfer.files[0]); }}>
          <span className="acc-drop-icon"><Icon name="upload" size={24} /></span>
          <span><strong>{t('documentsPage.add')}</strong><br /><span className="muted small">{t('documentsPage.formats')}</span></span>
          <input type="file" accept={ACCEPT} className="sr-only" onChange={(e) => { pick(e.target.files[0]); e.target.value = ''; }} />
        </label>
      ) : (
        <section className="card">
          <h2 className="acc-card-title">{t('documentsPage.describe', { name: pending.file.name })}</h2>
          <p className="muted small">{kb(pending.file.size)}</p>
          {metaForm(pending.meta, (m) => setPending({ ...pending, meta: m }), 'new')}
          <div className="editor-actions" style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-ghost" onClick={() => setPending(null)} disabled={busy}>{t('common.cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={upload} disabled={busy}>{busy ? t('common.sending') : t('documentsPage.send')}</button>
          </div>
        </section>
      )}

      {!docs && !error && <p className="muted">{t('common.loading')}</p>}
      {docs && docs.length === 0 && <p className="muted">{t('documents.none')}</p>}
      {docs && docs.length > 0 && (
        <ul className="acc-docs">
          {docs.map((d) => {
            const left = daysTo(d.expiresOn);
            return (
              <li key={d.id} className="acc-doc">
                <span className="acc-doc-icon" aria-hidden="true">{d.mime === 'application/pdf' ? 'PDF' : 'IMG'}</span>
                <div>
                  <div className="acc-doc-title"><strong>{t(`documentsPage.types.${d.type}`)}</strong><span className={`chip-status chip-${d.status}`}>{t(`documentsPage.status.${d.status}`)}</span>
                    <span className="chip-status chip-read">{d.visibility === 'public' ? t('documentsPage.vis.public') : t('documentsPage.vis.privateShort')}</span></div>
                  <div className="acc-doc-meta">
                    <span>{d.name} · {kb(d.size)}</span>
                    {d.number && <span>{t('documentsPage.numberShort', { n: d.number })}</span>}
                    {d.issuedOn && <span>{t('documentsPage.issued', { date: formatDate(d.issuedOn, lang) })}</span>}
                    {d.expiresOn && <span className={left < 0 ? 'acc-doc-expired' : left <= 30 ? 'acc-doc-expiring' : ''}>{left < 0 ? t('documentsPage.expired', { date: formatDate(d.expiresOn, lang) }) : t('documentsPage.expires', { date: formatDate(d.expiresOn, lang) })}</span>}
                  </div>
                  {d.status === 'rejected' && d.reviewReason && <p className="acc-reason">{t('documentsPage.rejectedReason', { reason: d.reviewReason })}</p>}
                </div>
                <div className="acc-actions">
                  <button type="button" className="acc-icon-btn" title={t('common.view')} aria-label={`${t('common.view')} — ${d.name}`} onClick={() => openPrivateFile(`/membership/documents/${d.id}/file`).catch(() => setError(t('errors.server_error')))}><Icon name="eye" size={18} /></button>
                  <button type="button" className="acc-icon-btn" title={t('common.edit')} aria-label={`${t('common.edit')} — ${d.name}`} onClick={() => setEditing({ id: d.id, meta: { type: d.type, number: d.number, issuedOn: d.issuedOn || '', expiresOn: d.expiresOn || '', visibility: d.visibility } })}><Icon name="edit" size={18} /></button>
                  <button type="button" className="acc-icon-btn is-danger" title={t('common.delete')} aria-label={`${t('common.delete')} — ${d.name}`} onClick={() => setConfirm(d)}><Icon name="trash" size={18} /></button>
                </div>
                {confirm?.id === d.id && (
                  <div className="acc-doc-form acc-confirm" role="alertdialog" aria-label={t('documents.confirmDelete', { name: d.name })}>
                    <p>{t('documents.confirmDelete', { name: d.name })}</p>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirm(null)} autoFocus>{t('common.cancel')}</button>
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => remove(d)} disabled={busy}>{t('common.delete')}</button>
                  </div>
                )}
                {editing?.id === d.id && (
                  <div className="acc-doc-form">
                    {d.status === 'verified' && <p className="muted small">{t('documentsPage.editVerified')}</p>}
                    {metaForm(editing.meta, (m) => setEditing({ ...editing, meta: m }), `e${d.id}`)}
                    <div className="editor-actions" style={{ marginTop: 10 }}>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>{t('common.cancel')}</button>
                      <button type="button" className="btn btn-primary btn-sm" onClick={saveMeta} disabled={busy}>{t('common.save')}</button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
